import { useEffect, useRef, useState } from "react";

export type EmailSuggestItem = {
  email: string;
  label?: string | null;
  detail?: string | null;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  exclude?: string[];
  company?: string | null;
  serial?: string | null;
  mac?: string | null;
};

export function EmailPredictiveInput({
  value,
  onChange,
  onEnter,
  placeholder = "メールアドレス",
  className = "support-contact-input",
  autoFocus = false,
  exclude = [],
  company,
  serial,
  mac,
}: Props) {
  const [items, setItems] = useState<EmailSuggestItem[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const blurTimerRef = useRef<number | null>(null);
  const excludeLower = exclude.map((x) => x.trim().toLowerCase()).filter(Boolean);

  useEffect(() => {
    const q = value.trim();
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (q.length < 2) {
      abortRef.current?.abort();
      setItems([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    timerRef.current = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      try {
        const qs = new URLSearchParams({ q, limit: "10" });
        if (company?.trim()) qs.set("company", company.trim());
        if (serial?.trim()) qs.set("serial", serial.trim());
        if (mac?.trim()) qs.set("mac", mac.trim());
        const res = await fetch(`/api/support/contact-email-suggest?${qs.toString()}`, {
          signal: ac.signal,
          credentials: "include",
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as { emails?: EmailSuggestItem[] };
        if (ac.signal.aborted) return;
        const list = (Array.isArray(data.emails) ? data.emails : [])
          .filter((x) => x.email && !excludeLower.includes(x.email.toLowerCase()));
        setItems(list);
        setHighlight(0);
        setOpen(list.length > 0 && !(list.length === 1 && list[0].email === q.toLowerCase()));
      } catch (e) {
        if ((e as Error)?.name === "AbortError") return;
        setItems([]);
        setOpen(false);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, 200);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [value, company, serial, mac, excludeLower.join("|")]);

  const apply = (email: string) => {
    setOpen(false);
    setItems([]);
    onChange(email);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (open && items.length > 0) {
        e.preventDefault();
        apply(items[highlight]?.email || value);
        return;
      }
      onEnter?.();
      return;
    }
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="support-email-predictive" style={{ flex: 1, minWidth: 0, position: "relative" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        onFocus={() => {
          if (blurTimerRef.current) {
            window.clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
          }
          if (items.length > 0) setOpen(true);
        }}
        onBlur={() => {
          blurTimerRef.current = window.setTimeout(() => {
            blurTimerRef.current = null;
            setOpen(false);
          }, 150);
        }}
        onKeyDown={onKeyDown}
      />
      {open && items.length > 0 && (
        <div className="support-email-predictive__list" role="listbox" aria-label="メール候補">
          {items.map((item, idx) => (
            <button
              key={item.email}
              type="button"
              role="option"
              aria-selected={idx === highlight}
              className={`support-email-predictive__item${idx === highlight ? " is-active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => apply(item.email)}
            >
              <span className="support-email-predictive__email">{item.email}</span>
              {(item.label || item.detail) && (
                <span className="support-email-predictive__meta">
                  {[item.label, item.detail].filter(Boolean).join(" · ")}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {loading && value.trim().length >= 2 && !open && (
        <span className="support-email-predictive__loading" aria-hidden>…</span>
      )}
    </div>
  );
}